// components/CustomBooking.tsx - With localStorage persistence
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
    Hotel, Users, Calendar, UserPlus, Send, X, CheckCircle,
    AlertCircle, Loader2, ChevronDown, ChevronRight, Bed,
    User, Mail, Phone, Globe, Clock, FileText, ArrowLeft, UserCog,
    MessageSquare,
} from 'lucide-react';
import { FaChild } from 'react-icons/fa6';

// ============================================================
// TYPES
// ============================================================
export interface Room {
    id: string;
    name: string;
    label: string;
    capacity: number;
    beds: number;
    bedType?: string;
    bedTypes?: string[];      // Added
    bedQuantities?: number[]; // Added
    bedsData?: any[];         // Added - original beds data
    price: number;
    provider?: string;
    description?: string;
    image?: string;
    reservedDates?: string[];
    amenities?: string[];
}

export interface Member {
    id: string;
    name: string;
    age?: number;
    isChild?: boolean;
    roomId?: string;
}

export interface RoomAssignment {
    roomId: string;
    members: Member[];
}

export interface BookingRequest {
    checkIn: string;
    checkOut: string;
    rooms: RoomAssignment[];
    totalGuests: number;
    totalChildren: number;
    totalPrice: number;
    guestDetails: {
        fullName: string;
        email: string;
        phone: string;
        country: string;
    };
    specialRequests?: string;
}

export interface CustomBookingProps {
    isOpen: boolean;
    onClose: () => void;
    rooms: Room[];
    endpoint: string;
    method?: 'POST' | 'PUT' | 'PATCH';
    title?: string;
    subtitle?: string;
    submitLabel?: string;
    cancelLabel?: string;
    successMessage?: string;
    countryOptions?: Array<{ value: string; label: string }>;
    currencySymbol?: string;
    storageKey?: string;
    onSuccess?: (data: BookingRequest, response: any) => void;
    onError?: (error: Error) => void;
    className?: string;
}

// ============================================================
// CONSTANTS
// ============================================================
const DEFAULT_COUNTRIES = [
    { value: 'US', label: 'United States' }, { value: 'GB', label: 'United Kingdom' },
    { value: 'CA', label: 'Canada' }, { value: 'AU', label: 'Australia' },
    { value: 'IN', label: 'India' }, { value: 'AE', label: 'United Arab Emirates' },
    { value: 'SA', label: 'Saudi Arabia' }, { value: 'MV', label: 'Maldives' },
    { value: 'MY', label: 'Malaysia' }, { value: 'SG', label: 'Singapore' },
    { value: 'LK', label: 'Sri Lanka' }, { value: 'BD', label: 'Bangladesh' },
    { value: 'PK', label: 'Pakistan' }, { value: 'EG', label: 'Egypt' },
    { value: 'TR', label: 'Turkey' }, { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' }, { value: 'IT', label: 'Italy' },
    { value: 'ES', label: 'Spain' }, { value: 'NL', label: 'Netherlands' },
    { value: 'CH', label: 'Switzerland' }, { value: 'SE', label: 'Sweden' },
    { value: 'NO', label: 'Norway' }, { value: 'DK', label: 'Denmark' },
    { value: 'FI', label: 'Finland' }, { value: 'JP', label: 'Japan' },
    { value: 'CN', label: 'China' }, { value: 'KR', label: 'South Korea' },
    { value: 'ID', label: 'Indonesia' }, { value: 'PH', label: 'Philippines' },
    { value: 'TH', label: 'Thailand' }, { value: 'VN', label: 'Vietnam' },
    { value: 'ZA', label: 'South Africa' }, { value: 'NG', label: 'Nigeria' },
    { value: 'KE', label: 'Kenya' }, { value: 'BR', label: 'Brazil' },
    { value: 'AR', label: 'Argentina' }, { value: 'MX', label: 'Mexico' },
    { value: 'RU', label: 'Russia' }, { value: 'PL', label: 'Poland' },
    { value: 'PT', label: 'Portugal' }, { value: 'GR', label: 'Greece' },
    { value: 'NZ', label: 'New Zealand' },
];

// ============================================================
// STORAGE HELPERS
// ============================================================
const STORAGE_KEY = 'custom_booking_data';

interface StoredData {
    checkIn: string;
    checkOut: string;
    roomAssignments: { roomId: string; members: Member[] }[];
    guestDetails: { fullName: string; email: string; phone: string; country: string };
    specialRequests: string;
    step: number;
    timestamp: number;
}

const saveToStorage = (data: Partial<StoredData>) => {
    try {
        const existing = localStorage.getItem(STORAGE_KEY);
        const parsed = existing ? JSON.parse(existing) : {};
        const merged = { ...parsed, ...data, timestamp: Date.now() };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    } catch (error) {
        console.error('Failed to save to localStorage:', error);
    }
};

const loadFromStorage = (): StoredData | null => {
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        if (!data) return null;
        const parsed = JSON.parse(data);
        // Check if data is older than 24 hours (86400000 ms)
        if (Date.now() - parsed.timestamp > 86400000) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return parsed;
    } catch (error) {
        console.error('Failed to load from localStorage:', error);
        return null;
    }
};

const clearStorage = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        console.error('Failed to clear localStorage:', error);
    }
};

// ============================================================
// REUSABLE COMPONENTS
// ============================================================
const ErrorMessage = ({ message }: { message: string | null }) =>
    message ? (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 mb-4">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">{message}</p>
        </div>
    ) : null;

const StepIndicator = ({ currentStep }: { currentStep: number }) => {
    const steps = [
        { num: 1, label: 'Select Rooms' },
        { num: 2, label: 'Guest Details' },
    ];

    return (
        <div className="flex items-center gap-4 mt-4">
            {steps.map((step, idx) => (
                <React.Fragment key={step.num}>
                    <div className={`flex items-center gap-2 ${currentStep >= step.num ? 'text-blue-600' : 'text-gray-400'}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${currentStep >= step.num ? 'bg-blue-600 text-white' : 'bg-gray-200'
                            }`}>
                            {step.num}
                        </div>
                        <span className="text-sm">{step.label}</span>
                    </div>
                    {idx < steps.length - 1 && (
                        <div className={`flex-1 h-0.5 ${currentStep > step.num ? 'bg-blue-600' : 'bg-gray-200'}`} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
};

const FormField = ({
    icon: Icon, label, type = 'text', value, onChange, placeholder, required, max, min
}: any) => (
    <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
            {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />}
            {type === 'select' ? (
                <select
                    value={value}
                    onChange={onChange}
                    className="w-full pl-11 pr-10 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition outline-none appearance-none bg-white"
                    required={required}
                >
                    <option value="">Select...</option>
                    {placeholder?.map((opt: any) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            ) : (
                <input
                    type={type}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    min={min}
                    max={max}
                    className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition outline-none"
                    required={required}
                />
            )}
            {type === 'select' && <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />}
        </div>
    </div>
);

const SectionCard = ({ icon: Icon, title, children, className = '' }: any) => (
    <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200 ${className}`}>
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            {Icon && <Icon className="w-5 h-5 text-blue-500" />}
            {title}
        </h3>
        {children}
    </div>
);

const SummaryRow = ({ label, value, className = '' }: any) => (
    <div className={`flex justify-between ${className}`}>
        <span className="text-gray-500">{label}</span>
        <span className="font-medium">{value}</span>
    </div>
);

// ============================================================
// MAIN COMPONENT
// ============================================================
const CustomBooking = ({
    isOpen,
    onClose,
    rooms,
    endpoint,
    method = 'POST',
    title = 'Custom Booking',
    subtitle = 'Select rooms and assign members for your stay',
    submitLabel = 'Send Request',
    cancelLabel = 'Cancel',
    successMessage = 'Your booking request has been sent successfully!',
    countryOptions = DEFAULT_COUNTRIES,
    currencySymbol = '$',
    storageKey = STORAGE_KEY,
    onSuccess,
    onError,
    className = '',
}: CustomBookingProps) => {
    // ============ STATE ============
    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [bookingId, setBookingId] = useState<string | null>(null);
    const [expandedRoom, setExpandedRoom] = useState<string | null>(null);
    const [checkIn, setCheckIn] = useState('');
    const [checkOut, setCheckOut] = useState('');
    const [roomAssignments, setRoomAssignments] = useState<Map<string, Member[]>>(new Map());
    const [guestDetails, setGuestDetails] = useState({ fullName: '', email: '', phone: '', country: '' });
    const [specialRequests, setSpecialRequests] = useState('');
    const [tempMember, setTempMember] = useState({ name: '', age: '', isChild: false });
    const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Refs for input focus
    const nameInputRef = useRef<HTMLInputElement>(null);

    // ============ MEMOIZED ============
    const totalGuests = useMemo(() =>
        Array.from(roomAssignments.values()).reduce((sum, members) => sum + members.filter(m => !m.isChild).length, 0),
        [roomAssignments]
    );

    const totalChildren = useMemo(() =>
        Array.from(roomAssignments.values()).reduce((sum, members) => sum + members.filter(m => m.isChild).length, 0),
        [roomAssignments]
    );

    const totalPeople = useMemo(() =>
        Array.from(roomAssignments.values()).reduce((sum, members) => sum + members.length, 0),
        [roomAssignments]
    );

    const totalRooms = useMemo(() =>
        Array.from(roomAssignments.values()).filter(members => members.length > 0).length,
        [roomAssignments]
    );

    const nights = useMemo(() => {
        if (!checkIn || !checkOut) return 0;
        return Math.ceil((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / (1000 * 60 * 60 * 24));
    }, [checkIn, checkOut]);

    const totalPrice = useMemo(() => {
        if (!nights) return 0;
        let price = 0;
        roomAssignments.forEach((members, roomId) => {
            const room = rooms.find(r => r.id === roomId);
            if (room && members.length > 0) price += room.price * nights;
        });
        return price;
    }, [roomAssignments, nights, rooms]);

    const availableRooms = useMemo(() => {
        if (!checkIn || !checkOut) return rooms;
        const checkInDate = new Date(checkIn);
        const checkOutDate = new Date(checkOut);
        return rooms.filter(room => {
            if (!room.reservedDates) return true;
            return !room.reservedDates.some(reserved => {
                const d = new Date(reserved);
                return d >= checkInDate && d < checkOutDate;
            });
        });
    }, [rooms, checkIn, checkOut]);

    const selectedRooms = useMemo(() => {
        const selected: any[] = [];
        roomAssignments.forEach((members, roomId) => {
            if (members.length > 0) {
                const room = rooms.find(r => r.id === roomId);
                if (room) selected.push({ ...room, members });
            }
        });
        return selected;
    }, [roomAssignments, rooms]);

    // ============ LOAD SAVED DATA ============
    useEffect(() => {
        if (isOpen) {
            const savedData = loadFromStorage();
            if (savedData) {
                // Restore check-in/check-out
                setCheckIn(savedData.checkIn || '');
                setCheckOut(savedData.checkOut || '');

                // Restore guest details
                setGuestDetails(savedData.guestDetails || { fullName: '', email: '', phone: '', country: '' });

                // Restore special requests
                setSpecialRequests(savedData.specialRequests || '');

                // Restore step
                setStep(savedData.step || 1);

                // Restore room assignments
                if (savedData.roomAssignments && savedData.roomAssignments.length > 0) {
                    const newAssignments = new Map();
                    savedData.roomAssignments.forEach(({ roomId, members }) => {
                        newAssignments.set(roomId, members);
                    });
                    setRoomAssignments(newAssignments);
                } else {
                    // Initialize empty assignments
                    const newAssignments = new Map();
                    rooms.forEach(room => newAssignments.set(room.id, []));
                    setRoomAssignments(newAssignments);
                }
            } else {
                // Initialize empty assignments
                const newAssignments = new Map();
                rooms.forEach(room => newAssignments.set(room.id, []));
                setRoomAssignments(newAssignments);
            }
            setIsLoading(false);
        }
    }, [isOpen, rooms]);

    // ============ SAVE TO STORAGE ============
    useEffect(() => {
        if (!isOpen || isLoading || isSuccess) return;

        // Save to localStorage with debounce
        const timeoutId = setTimeout(() => {
            const assignmentsArray = Array.from(roomAssignments.entries()).map(([roomId, members]) => ({
                roomId,
                members,
            }));

            saveToStorage({
                checkIn,
                checkOut,
                roomAssignments: assignmentsArray,
                guestDetails,
                specialRequests,
                step,
            });
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [isOpen, isLoading, isSuccess, checkIn, checkOut, roomAssignments, guestDetails, specialRequests, step]);

    // ============ EFFECTS ============
    // Focus input when add member is opened
    useEffect(() => {
        if (activeRoomId && nameInputRef.current) {
            setTimeout(() => {
                nameInputRef.current?.focus();
            }, 100);
        }
    }, [activeRoomId]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen && !isSuccess) {
                if (activeRoomId) {
                    setActiveRoomId(null);
                    setTempMember({ name: '', age: '', isChild: false });
                } else {
                    onClose();
                }
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose, isSuccess, activeRoomId]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : 'unset';
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    // ============ HANDLERS ============
    const getRoomAvailability = useCallback((room: Room) => {
        if (!checkIn || !checkOut || !room.reservedDates) return true;
        const checkInDate = new Date(checkIn);
        const checkOutDate = new Date(checkOut);
        return !room.reservedDates.some(reserved => {
            const d = new Date(reserved);
            return d >= checkInDate && d < checkOutDate;
        });
    }, [checkIn, checkOut]);

    const handleAddMember = useCallback((roomId: string) => {
        const room = rooms.find(r => r.id === roomId);
        if (!room) return;

        const currentMembers = roomAssignments.get(roomId) || [];
        if (currentMembers.length >= room.capacity) {
            setError(`Room capacity is ${room.capacity} guests`);
            return;
        }

        const name = tempMember.name.trim();
        if (!name) {
            setError('Please enter a name');
            return;
        }

        const newMember: Member = {
            id: `m-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
            name: name,
            age: tempMember.age ? parseInt(tempMember.age) : undefined,
            isChild: tempMember.isChild || false,
            roomId,
        };

        const updated = new Map(roomAssignments);
        updated.set(roomId, [...currentMembers, newMember]);
        setRoomAssignments(updated);

        // Clear form after successful addition
        setTempMember({ name: '', age: '', isChild: false });
        setActiveRoomId(null);
        setError(null);

        // Keep the room expanded to show the new member
        setExpandedRoom(roomId);

        // Save to storage immediately after adding
        const assignmentsArray = Array.from(updated.entries()).map(([rid, members]) => ({
            roomId: rid,
            members,
        }));
        saveToStorage({
            checkIn,
            checkOut,
            roomAssignments: assignmentsArray,
            guestDetails,
            specialRequests,
            step,
        });
    }, [roomAssignments, rooms, tempMember, checkIn, checkOut, guestDetails, specialRequests, step]);

    const handleRemoveMember = useCallback((roomId: string, memberId: string) => {
        const updated = new Map(roomAssignments);
        updated.set(roomId, (updated.get(roomId) || []).filter(m => m.id !== memberId));
        setRoomAssignments(updated);
    }, [roomAssignments]);

    const handleClearRoom = useCallback((roomId: string) => {
        const updated = new Map(roomAssignments);
        updated.set(roomId, []);
        setRoomAssignments(updated);
    }, [roomAssignments]);

    const handleNext = useCallback(() => {
        if (!checkIn || !checkOut) {
            setError('Please select check-in and check-out dates');
            return;
        }
        if (totalPeople === 0) {
            setError('Please assign at least one guest to a room');
            return;
        }
        setError(null);
        setStep(2);
    }, [checkIn, checkOut, totalPeople]);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        const { fullName, email, phone, country } = guestDetails;
        if (!fullName.trim()) return setError('Full name is required');
        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError('Valid email is required');
        if (!phone.trim()) return setError('Phone number is required');
        if (!country) return setError('Country is required');

        setIsSubmitting(true);
        const bookingRequest: BookingRequest = {
            checkIn, checkOut,
            rooms: selectedRooms.map(r => ({
                roomId: r.id,
                members: r.members.map((m: any) => ({
                    id: m.id,
                    name: m.name,
                    age: m.age,
                    isChild: m.isChild || false
                }))
            })),
            totalGuests,
            totalChildren,
            totalPrice,
            guestDetails,
            specialRequests: specialRequests.trim() || undefined,
        };

        try {
            const response = await fetch(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...bookingRequest, submittedAt: new Date().toISOString() }),
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message || 'Booking failed');

            setIsSuccess(true);
            setBookingId(result.bookingId || null);
            // Clear storage on success
            clearStorage();
            onSuccess?.(bookingRequest, result);
        } catch (err: any) {
            setError(err.message);
            onError?.(err);
        } finally {
            setIsSubmitting(false);
        }
    }, [guestDetails, checkIn, checkOut, selectedRooms, totalGuests, totalChildren, totalPrice, specialRequests, endpoint, method]);

    const handleReset = useCallback(() => {
        setStep(1);
        setCheckIn('');
        setCheckOut('');
        const newAssignments = new Map();
        rooms.forEach(room => newAssignments.set(room.id, []));
        setRoomAssignments(newAssignments);
        setGuestDetails({ fullName: '', email: '', phone: '', country: '' });
        setSpecialRequests('');
        setError(null);
        setIsSuccess(false);
        setBookingId(null);
        setTempMember({ name: '', age: '', isChild: false });
        setActiveRoomId(null);
        setExpandedRoom(null);
        clearStorage();
    }, [rooms]);

    const handleClose = useCallback(() => {
        if (isSuccess) {
            handleReset();
        }
        onClose();
    }, [isSuccess, handleReset, onClose]);

    const handleCancelAddMember = useCallback(() => {
        setActiveRoomId(null);
        setTempMember({ name: '', age: '', isChild: false });
        setError(null);
    }, []);

    if (!isOpen) return null;

    // ============================================================
    // RENDER HELPERS
    // ============================================================
    const renderRoomCard = (room: Room) => {
  const members = roomAssignments.get(room.id) || [];
  const isAvailable = getRoomAvailability(room);
  const isFull = members.length >= room.capacity;
  const isExpanded = expandedRoom === room.id;
  const isAddingMember = activeRoomId === room.id;

  // Get aggregated bed display - combines duplicate bed types
  const getAggregatedBedDisplay = () => {
    if (room.bedsData && room.bedsData.length > 0) {
      // Aggregate quantities by bed type
      const bedMap = new Map<string, number>();
      room.bedsData.forEach(bed => {
        const current = bedMap.get(bed.type) || 0;
        bedMap.set(bed.type, current + bed.quantity);
      });
      
      // Convert to array of strings
      return Array.from(bedMap.entries()).map(([type, quantity]) => 
        `${quantity}x ${type}`
      ).join(', ');
    }
    return `${room.beds} bed${room.beds > 1 ? 's' : ''}`;
  };

  // Get bed icons with aggregated display
  const getBedIcons = () => {
    if (room.bedsData && room.bedsData.length > 0) {
      // Aggregate quantities by bed type for icons too
      const bedMap = new Map<string, number>();
      room.bedsData.forEach(bed => {
        const current = bedMap.get(bed.type) || 0;
        bedMap.set(bed.type, current + bed.quantity);
      });
      
      // Show up to 4 bed icons total
      const totalBeds = Array.from(bedMap.values()).reduce((sum, qty) => sum + qty, 0);
      return (
        <div className="flex items-center gap-0.5">
          {Array.from({ length: Math.min(totalBeds, 4) }).map((_, i) => (
            <Bed key={i} className="w-3.5 h-3.5 text-blue-500" />
          ))}
          {totalBeds > 4 && (
            <span className="text-[10px] font-semibold text-blue-600 ml-0.5">+{totalBeds - 4}</span>
          )}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-0.5">
        {Array.from({ length: Math.min(room.beds, 4) }).map((_, i) => (
          <Bed key={i} className="w-3.5 h-3.5 text-blue-500" />
        ))}
        {room.beds > 4 && (
          <span className="text-[10px] font-semibold text-blue-600 ml-0.5">+{room.beds - 4}</span>
        )}
      </div>
    );
  };

  return (
    <div key={room.id} className={`border rounded-xl overflow-hidden transition-all ${
      !isAvailable ? 'bg-gray-50 border-gray-200 opacity-60' :
      members.length > 0 ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200 hover:border-blue-200'
    }`}>
      <div className="p-4 cursor-pointer flex items-center justify-between" onClick={() => setExpandedRoom(isExpanded ? null : room.id)}>
        <div className="flex items-center gap-3">
          <Bed className="w-5 h-5 text-blue-500" />
          <div>
            <h4 className="font-medium text-gray-800">{room.label}</h4>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>{room.capacity} guests</span>
              <span>•</span>
              {getBedIcons()}
              <span className="text-xs text-gray-400 ml-1">
                ({getAggregatedBedDisplay()})
              </span>
              {!isAvailable && <span className="text-red-500 text-xs">Unavailable</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-800">{currencySymbol}{room.price}/night</span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </div>


      {/* <div className="">
         {room.description && <p className="text-sm text-gray-600">{room.description}</p>}           
      </div> */}

                {isExpanded && (
                    <div className="px-4 pb-4 space-y-3">
                       {room.amenities?.length && (
                            <div className="flex flex-wrap gap-1">
                                {room.amenities.map((a, i) => (
                                    <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{a}</span>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className="px-4 pb-4">
                    {members.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                            {members.map(m => (
                                <div key={m.id} className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${m.isChild ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                                    }`}>
                                    {m.isChild ? <FaChild className="w-3 h-3" /> : <User className="w-3 h-3" />}
                                    <span>{m.name}</span>
                                    {m.age && <span className="text-xs">({m.age})</span>}
                                    {m.isChild && <span className="text-xs">Child</span>}
                                    <button onClick={() => handleRemoveMember(room.id, m.id)} className="hover:text-red-600">
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {isAvailable && (
                        <div className="flex gap-2 flex-wrap">
                            {isAddingMember ? (
                                <div className="flex-1 flex flex-wrap gap-2 items-center">
                                    <input
                                        ref={nameInputRef}
                                        type="text"
                                        value={tempMember.name}
                                        onChange={e => setTempMember({ ...tempMember, name: e.target.value })}
                                        placeholder="Guest name"
                                        className="flex-1 min-w-[120px] px-3 py-1.5 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddMember(room.id);
                                            }
                                            if (e.key === 'Escape') {
                                                handleCancelAddMember();
                                            }
                                        }}
                                    />
                                    <input
                                        type="number"
                                        value={tempMember.age}
                                        onChange={e => setTempMember({ ...tempMember, age: e.target.value })}
                                        placeholder="Age"
                                        className="w-20 px-3 py-1.5 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm"
                                        min="0"
                                        max="120"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddMember(room.id);
                                            }
                                        }}
                                    />
                                    <label className="flex items-center gap-1 text-sm text-gray-600 whitespace-nowrap">
                                        <input
                                            type="checkbox"
                                            checked={tempMember.isChild}
                                            onChange={e => setTempMember({ ...tempMember, isChild: e.target.checked })}
                                            className="w-4 h-4"
                                        />
                                        Child
                                    </label>
                                    <button
                                        onClick={() => handleAddMember(room.id)}
                                        disabled={!tempMember.name.trim()}
                                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Add
                                    </button>
                                    <button
                                        onClick={handleCancelAddMember}
                                        className="px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 text-sm transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {!isFull && (
                                        <button
                                            onClick={() => { setActiveRoomId(room.id); setTempMember({ name: '', age: '', isChild: false }); }}
                                            className="flex items-center gap-1 px-3 py-1.5 border border-dashed border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 text-sm transition-colors"
                                        >
                                            <UserPlus className="w-3 h-3" /> Add Guest
                                        </button>
                                    )}
                                    {members.length > 0 && (
                                        <button
                                            onClick={() => handleClearRoom(room.id)}
                                            className="px-3 py-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg text-sm transition-colors"
                                        >
                                            Clear
                                        </button>
                                    )}
                                    <span className="text-xs text-gray-400 ml-auto">
                                        {members.length}/{room.capacity} capacity
                                    </span>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderSummary = () => (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                {[
                    { label: 'Rooms', value: totalRooms },
                    { label: 'Adults', value: totalGuests },
                    { label: 'Children', value: totalChildren },
                    { label: 'Nights', value: nights },
                    { label: 'Total', value: `${currencySymbol}${totalPrice}` },
                ].map((item, i) => (
                    <div key={i}>
                        <span className="text-gray-500">{item.label}</span>
                        <p className="font-semibold text-gray-800">{item.value}</p>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderBookingSummary = () => (
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-500" /> Booking Summary
            </h4>
            <div className="space-y-1 text-sm">
                <SummaryRow label="Check-in" value={new Date(checkIn).toLocaleDateString()} />
                <SummaryRow label="Check-out" value={new Date(checkOut).toLocaleDateString()} />
                <SummaryRow label="Nights" value={nights} />
                <SummaryRow label="Adults" value={totalGuests} />
                <SummaryRow label="Children" value={totalChildren} />
                <SummaryRow label="Rooms" value={totalRooms} />
                {selectedRooms.map(room => (
                    <div key={room.id} className="flex justify-between pl-4 text-xs">
                        <span className="text-gray-400">{room.label}</span>
                        <span className="text-gray-600">{room.members.length} guests</span>
                    </div>
                ))}
                <SummaryRow label="Total" value={`${currencySymbol}${totalPrice}`} className="pt-2 border-t border-gray-200 font-bold" />
            </div>
        </div>
    );

    // ============================================================
    // RENDER
    // ============================================================
    return (
        <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className={`bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-auto relative ${className}`}>
                <button onClick={handleClose} className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 z-10">
                    <X className="w-5 h-5" />
                </button>

                <div className="p-6 md:p-8">
                    {isSuccess ? (
                        <div className="text-center py-8">
                            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                                <CheckCircle className="w-10 h-10 text-green-600" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-2">Request Sent!</h3>
                            <p className="text-gray-600 mb-4">{successMessage}</p>
                            {bookingId && (
                                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm mb-4">
                                    Booking Reference: <strong>{bookingId}</strong>
                                </div>
                            )}
                            <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200 text-left">
                                <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-blue-500" /> Booking Summary
                                </p>
                                <div className="space-y-2 text-sm">
                                    <SummaryRow label="Check-in" value={new Date(checkIn).toLocaleDateString()} />
                                    <SummaryRow label="Check-out" value={new Date(checkOut).toLocaleDateString()} />
                                    <SummaryRow label="Adults" value={totalGuests} />
                                    <SummaryRow label="Children" value={totalChildren} />
                                    <SummaryRow label="Rooms" value={totalRooms} />
                                    {selectedRooms.map(room => (
                                        <div key={room.id} className="flex justify-between pl-4 border-t border-gray-100 pt-1 mt-1">
                                            <span className="text-gray-600">{room.label}</span>
                                            <span className="font-medium text-gray-800">
                                                {room.members.map((m: any) => `${m.name}${m.isChild ? ' (Child)' : ''}`).join(', ')}
                                            </span>
                                        </div>
                                    ))}
                                    <SummaryRow label="Total" value={`${currencySymbol}${totalPrice}`} className="pt-2 border-t border-gray-200 font-bold" />
                                </div>
                            </div>
                            <p className="text-sm text-gray-500 mt-4 mb-6">A confirmation has been sent to {guestDetails.email}</p>
                            <button onClick={handleClose} className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-medium">
                                Close
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="mb-6">
                                <div className="flex items-center gap-3 mb-2">
                                    <Hotel className="w-6 h-6 text-blue-600" />
                                    <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
                                </div>
                                <p className="text-gray-500 text-sm">{subtitle}</p>
                                <StepIndicator currentStep={step} />
                            </div>

                            <ErrorMessage message={error} />

                            {step === 1 && (
                                <div className="space-y-6 animate-slide-in">
                                    <SectionCard icon={Calendar} title="Select Your Dates">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Date *</label>
                                                <input type="date" value={checkIn} onChange={e => setCheckIn(e.target.value)}
                                                    min={new Date().toISOString().split('T')[0]}
                                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Date *</label>
                                                <input type="date" value={checkOut} onChange={e => setCheckOut(e.target.value)}
                                                    min={checkIn || new Date().toISOString().split('T')[0]}
                                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none" />
                                            </div>
                                        </div>
                                        {nights > 0 && <div className="mt-2 text-sm text-gray-600"><Clock className="w-4 h-4 inline mr-1" />{nights} night{nights > 1 ? 's' : ''} stay</div>}
                                    </SectionCard>

                                    <div>
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                                                <Users className="w-5 h-5 text-blue-500" /> Assign Guests to Rooms
                                            </h3>
                                            <div className="text-sm text-gray-500">
                                                {totalPeople} guest{totalPeople > 1 ? 's' : ''} assigned
                                                {totalChildren > 0 && ` (${totalChildren} child${totalChildren > 1 ? 'ren' : ''})`}
                                            </div>
                                        </div>
                                        <div className="space-y-4">{availableRooms.map(renderRoomCard)}</div>
                                        {totalPeople > 0 && checkIn && checkOut && renderSummary()}
                                    </div>

                                    <div className="flex justify-end pt-4 border-t border-gray-100">
                                        <button onClick={handleNext} className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg transition font-medium">
                                            Next <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {step === 2 && (
                                <form onSubmit={handleSubmit} className="space-y-4 animate-slide-in">
                                    <SectionCard icon={User} title="Your Details">
                                        <FormField icon={User} label="Full Name" type="text" value={guestDetails.fullName}
                                            onChange={e => setGuestDetails({ ...guestDetails, fullName: e.target.value })} placeholder="John Doe" required />
                                        <FormField icon={Mail} label="Email Address" type="email" value={guestDetails.email}
                                            onChange={e => setGuestDetails({ ...guestDetails, email: e.target.value })} placeholder="john@example.com" required />
                                        <FormField icon={Phone} label="Phone Number" type="tel" value={guestDetails.phone}
                                            onChange={e => setGuestDetails({ ...guestDetails, phone: e.target.value })} placeholder="+1 234 567 8900" required />
                                        <FormField icon={Globe} label="Country" type="select" value={guestDetails.country}
                                            onChange={e => setGuestDetails({ ...guestDetails, country: e.target.value })} placeholder={countryOptions} required />

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests</label>
                                            <div className="relative">
                                                <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                                <textarea value={specialRequests} onChange={e => setSpecialRequests(e.target.value)}
                                                    rows={3} placeholder="Any special requirements..."
                                                    className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none resize-y" />
                                            </div>
                                        </div>
                                    </SectionCard>

                                    {renderBookingSummary()}

                                    <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
                                        <button type="button" onClick={() => setStep(1)} className="px-6 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition font-medium">
                                            <ArrowLeft className="w-4 h-4 inline mr-2" /> Back
                                        </button>
                                        <button type="submit" disabled={isSubmitting}
                                            className="flex-1 min-w-[120px] flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg transition font-medium disabled:opacity-70">
                                            {isSubmitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending...</> : <><Send className="w-5 h-5" /> {submitLabel}</>}
                                        </button>
                                        <button type="button" onClick={handleClose} className="px-6 py-3 text-gray-500 hover:text-gray-700 transition font-medium">
                                            {cancelLabel}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </>
                    )}
                </div>
            </div>

            <style>{`
        @keyframes fade-in { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @keyframes slide-in { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
        .animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
        .animate-slide-in { animation: slide-in 0.3s ease-out forwards; }
      `}</style>
        </div>
    );
};

export default CustomBooking;